/**
 * Trier et compter une liste comme on la lit.
 *
 * Les listes du CRM n'avaient qu'une barre de recherche : pour retrouver une
 * ligne, il fallait se souvenir d'un mot et le taper. On veut pouvoir cliquer
 * « Brouillon · 4 » et voir les quatre, cliquer l'en-tête « Total » et voir les
 * plus gros en tête.
 *
 * Deux règles qui ne vont pas de soi :
 *
 *  - **Le vide va toujours à la fin**, dans un sens comme dans l'autre. Un devis
 *    sans date ne doit pas passer en tête d'un tri « du plus récent ».
 *  - **Les numéros se comparent comme des nombres** : DEV-2026-0010 vient après
 *    DEV-2026-0009, et « 2 » avant « 10 ».
 */

export type SensTri = 'asc' | 'desc';

export interface Tri<K extends string> {
  cle: K;
  sens: SensTri;
}

export type ValeurTri = string | number | null | undefined;

const COLLATION = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

function estVide(v: ValeurTri): v is null | undefined | '' {
  return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

function comparer(a: ValeurTri, b: ValeurTri): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return COLLATION.compare(String(a), String(b));
}

/**
 * Les lignes triées, sans toucher à l'original. À égalité, l'ordre d'arrivée
 * est gardé : retrier ne fait pas sauter les lignes.
 */
export function trierLignes<T, K extends string>(
  lignes: readonly T[],
  tri: Tri<K>,
  valeurs: Readonly<Record<K, (ligne: T) => ValeurTri>>,
): T[] {
  const lire = valeurs[tri.cle];
  const signe = tri.sens === 'asc' ? 1 : -1;
  return lignes
    .map((ligne, i) => ({ ligne, i, v: lire(ligne) }))
    .sort((x, y) => {
      const vx = estVide(x.v);
      const vy = estVide(y.v);
      if (vx || vy) return vx === vy ? x.i - y.i : vx ? 1 : -1;
      return signe * comparer(x.v, y.v) || x.i - y.i;
    })
    .map((x) => x.ligne);
}

/**
 * Le tri lu dans une adresse : `date` (croissant) ou `-date` (décroissant).
 * Une clé inconnue rend le tri par défaut — une adresse abîmée ne casse pas
 * l'écran.
 */
export function lireTri<K extends string>(
  brut: string | null,
  cles: readonly K[],
  defaut: Tri<K>,
): Tri<K> {
  if (brut === null || brut === '') return defaut;
  const sens: SensTri = brut.startsWith('-') ? 'desc' : 'asc';
  const cle = brut.replace(/^-/, '');
  return (cles as readonly string[]).includes(cle) ? { cle: cle as K, sens } : defaut;
}

/** Le tri écrit pour une adresse : l'inverse de `lireTri`. */
export function ecrireTri<K extends string>(tri: Tri<K>): string {
  return `${tri.sens === 'desc' ? '-' : ''}${tri.cle}`;
}

/**
 * Le tri après un clic sur l'en-tête `cle`.
 *
 * Un premier clic trie dans le sens naturel de la colonne — les dates et les
 * montants du plus grand au plus petit, les textes de A à Z. Un second clic
 * inverse.
 */
export function triApresClic<K extends string>(
  actuel: Tri<K>,
  cle: K,
  sensNaturel: SensTri,
): Tri<K> {
  if (actuel.cle !== cle) return { cle, sens: sensNaturel };
  return { cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' };
}

export interface CompteStatut {
  statut: string;
  nombre: number;
}

/**
 * Combien de lignes par statut, dans l'ordre où on les traite.
 *
 * Un statut absent de `ordre` — ajouté en base et pas encore ici — n'est pas
 * perdu : il arrive à la fin. Un statut à zéro n'est pas montré : une pastille
 * « Litige · 0 » n'aide personne à trouver une ligne.
 */
export function compterParStatut<T>(
  lignes: readonly T[],
  statutDe: (ligne: T) => string,
  ordre: readonly string[],
): CompteStatut[] {
  const comptes = new Map<string, number>();
  for (const l of lignes) {
    const s = statutDe(l);
    comptes.set(s, (comptes.get(s) ?? 0) + 1);
  }
  const connus = ordre.filter((s) => comptes.has(s));
  const inconnus = [...comptes.keys()].filter((s) => !ordre.includes(s)).sort();
  return [...connus, ...inconnus].map((statut) => ({ statut, nombre: comptes.get(statut) ?? 0 }));
}
